import React, { useState, useEffect, useRef, useCallback } from 'react';
import { X } from 'lucide-react';

export const GateEntrance = ({ onComplete }) => {
  const [isVisible, setIsVisible] = useState(true);
  const [isOpening, setIsOpening] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [videoFading, setVideoFading] = useState(false);
  const videoRef = useRef(null);
  const onCompleteRef = useRef(onComplete);
  onCompleteRef.current = onComplete;

  const finishTransition = useCallback(() => {
    setVideoFading(prev => {
      if (prev) return prev;
      setTimeout(() => {
        setIsVisible(false);
        sessionStorage.setItem('hasSeenGateEntrance', 'true');
        onCompleteRef.current();
      }, 200);
      return true;
    });
  }, []);

  useEffect(() => {
    const hasSeenEntrance = sessionStorage.getItem('hasSeenGateEntrance');

    if (hasSeenEntrance) {
      setIsVisible(false);
      onCompleteRef.current();
      return;
    }

    const openTimer = setTimeout(() => setIsOpening(true), 400);
    const videoTimer = setTimeout(() => setShowVideo(true), 1500);

    return () => {
      clearTimeout(openTimer);
      clearTimeout(videoTimer);
    };
  }, []);

  useEffect(() => {
    if (showVideo && videoRef.current) {
      videoRef.current.play().catch(() => {
        setTimeout(finishTransition, 3000);
      });
    }
  }, [showVideo, finishTransition]);

  if (!isVisible) return null;

  return (
    <>
      {/* Gate Doors */}
      {!showVideo && (
        <div className="fixed inset-0 z-[100] bg-black">
          <div
            className={`absolute inset-y-0 left-0 w-1/2 transition-transform duration-[1000ms] ease-in-out overflow-hidden ${isOpening ? '-translate-x-full' : 'translate-x-0'}`}
            style={{ background: 'linear-gradient(to right, #1f2937, #374151, #1f2937)', boxShadow: 'inset -20px 0 40px rgba(0,0,0,0.5), 8px 0 20px rgba(0,0,0,0.8)' }}
          >
            <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 8px, rgba(0,0,0,0.4) 8px, rgba(0,0,0,0.4) 10px)' }}></div>
            <div className="absolute top-10 left-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute top-10 right-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute bottom-10 left-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute bottom-10 right-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute right-0 top-1/2 transform -translate-y-1/2 w-8 h-32 bg-gradient-to-r from-gray-900 to-gray-800 shadow-2xl">
              <div className="w-full h-full flex flex-col justify-around items-center">
                <div className="w-3 h-3 bg-gray-950 rounded-full"></div>
                <div className="w-3 h-3 bg-gray-950 rounded-full"></div>
                <div className="w-3 h-3 bg-gray-950 rounded-full"></div>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-end overflow-visible">
              <span className="select-none" style={{ fontFamily: "'Pacifico', cursive", fontSize: 'clamp(12rem, 35vh, 22rem)', lineHeight: 1, color: '#d4af37', textShadow: '0 0 30px rgba(95, 161, 159, 0.8), 0 0 60px rgba(212, 175, 55, 0.6), 4px 4px 8px rgba(0,0,0,0.5)', marginRight: '-0.5rem' }}>B</span>
            </div>
          </div>

          <div
            className={`absolute inset-y-0 right-0 w-1/2 transition-transform duration-[1000ms] ease-in-out overflow-hidden ${isOpening ? 'translate-x-full' : 'translate-x-0'}`}
            style={{ background: 'linear-gradient(to left, #1f2937, #374151, #1f2937)', boxShadow: 'inset 20px 0 40px rgba(0,0,0,0.5), -8px 0 20px rgba(0,0,0,0.8)' }}
          >
            <div className="absolute inset-0 opacity-40" style={{ backgroundImage: 'repeating-linear-gradient(90deg, transparent, transparent 8px, rgba(0,0,0,0.4) 8px, rgba(0,0,0,0.4) 10px)' }}></div>
            <div className="absolute top-10 left-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute top-10 right-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute bottom-10 left-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute bottom-10 right-10 w-4 h-4 bg-gray-900 rounded-full shadow-inner"></div>
            <div className="absolute left-0 top-1/2 transform -translate-y-1/2 w-8 h-32 bg-gradient-to-l from-gray-900 to-gray-800 shadow-2xl">
              <div className="w-full h-full flex flex-col justify-around items-center">
                <div className="w-3 h-3 bg-gray-950 rounded-full"></div>
                <div className="w-3 h-3 bg-gray-950 rounded-full"></div>
                <div className="w-3 h-3 bg-gray-950 rounded-full"></div>
              </div>
            </div>
            <div className="absolute inset-0 flex items-center justify-start overflow-visible">
              <span className="select-none" style={{ fontFamily: "'Pacifico', cursive", fontSize: 'clamp(12rem, 35vh, 22rem)', lineHeight: 1, color: '#d4af37', textShadow: '0 0 30px rgba(95, 161, 159, 0.8), 0 0 60px rgba(212, 175, 55, 0.6), 4px 4px 8px rgba(0,0,0,0.5)', marginLeft: '-0.5rem' }}>G</span>
            </div>
          </div>

          <div className="absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
            <div className={`w-20 h-28 bg-gradient-to-b from-gray-800 to-gray-900 rounded-lg shadow-2xl flex items-center justify-center transition-opacity duration-1000 ${isOpening ? 'opacity-0' : 'opacity-100'}`}>
              <div className="relative">
                <div className="w-6 h-6 bg-black rounded-full border-2 border-gold-500"></div>
                <div className="w-2 h-8 bg-black mx-auto border-l-2 border-r-2 border-gold-500"></div>
              </div>
            </div>
          </div>
          <div className="absolute inset-0 bg-gradient-radial from-teal-900/20 via-transparent to-transparent pointer-events-none"></div>
        </div>
      )}

      {/* Video Transition */}
      {showVideo && (
        <div className={`fixed inset-0 z-[100] transition-opacity duration-200 ${videoFading ? 'opacity-0' : 'opacity-100'}`}>
          <div className="relative w-full h-full bg-black">
            <video ref={videoRef} playsInline preload="auto" muted className="w-full h-full object-cover" onEnded={finishTransition}>
              <source src="/media/transition.mp4" type="video/mp4" />
            </video>
            <button
              type="button"
              aria-label="Skip transition"
              data-testid="skip-transition-btn"
              onClick={finishTransition}
              className="absolute top-6 right-6 pointer-events-auto flex items-center gap-2 px-3.5 py-2 rounded-full bg-black/50 hover:bg-black/70 backdrop-blur-md border border-white/20 hover:border-gold-500/60 text-white/80 hover:text-gold-300 text-xs uppercase tracking-widest font-semibold transition-all duration-200 shadow-lg opacity-70 hover:opacity-100"
            >
              Skip
              <X size={14} />
            </button>
          </div>
        </div>
      )}
    </>
  );
};
